/**
 * ChoosableTags — Horizontal tag filter bar.
 *
 * Matches v1's ChoosableTags component:
 * - "All" chip (active when no tags selected)
 * - One chip per available tag in the current view
 * - Click to toggle; multiple selection uses AND filtering
 * - Scroll horizontally on mobile, wrap on desktop
 *
 * Color logic:
 * - "All" selected (no active filter): muted/gray filled
 * - Specific tag selected: primary color
 * - Unselected tags: muted border/text
 *
 * Reads from / writes to the `useTagFilterStore` Zustand store.
 */

import { memo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import { useTagFilterStore } from "./tagFilterStore"

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ChoosableTags = memo(function ChoosableTags({
  className,
}: {
  className?: string
}) {
  const { t } = useTranslation()
  const availableTags = useTagFilterStore((s) => s.availableTags)
  const selectedTags = useTagFilterStore((s) => s.selectedTags)
  const toggleTag = useTagFilterStore((s) => s.toggleTag)
  const selectAll = useTagFilterStore((s) => s.selectAll)

  // Don't render if no tags exist
  if (availableTags.length === 0) return null

  const isAllSelected = selectedTags.length === 0

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 overflow-x-auto overflow-y-hidden desk:flex-wrap desk:overflow-x-visible",
        "mt-3",
        className,
      )}
    >
      <TagChip
        label={t("All")}
        selected={isAllSelected}
        onClick={selectAll}
      />
      {availableTags.map((tag) => (
        <TagChip
          key={tag}
          label={tag}
          selected={selectedTags.includes(tag)}
          onClick={() => toggleTag(tag)}
        />
      ))}
    </div>
  )
})

// ---------------------------------------------------------------------------
// Single tag chip
// ---------------------------------------------------------------------------

const TagChip = memo(function TagChip({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  const handleClick = useCallback(() => onClick(), [onClick])

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "inline-flex h-6 shrink-0 items-center rounded-full px-2.5 text-xs font-medium transition-colors",
        "cursor-pointer select-none",
        selected
          ? "bg-primary text-primary-foreground shadow-sm"
          : "border border-border text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {label}
    </button>
  )
})
